import { useState } from "react";
import { Select as AntSelect } from "antd";
import cuid from "cuid";
import { get } from "lodash";
import PropTypes from "prop-types";
import { useLazyQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { ACTION_TYPES } from "utils/constants";
import CanvasIcon from "./CanvasIcon";
import ConditionListEditor from "./ConditionListEditor";
import { ICON_PATHS } from "./step-look";

const SYNC_TYPES = [
  ACTION_TYPES.SYNC_PROCESSES,
  ACTION_TYPES.NO_SYNC_PROCESSES,
];

const isSync = (action) => SYNC_TYPES.includes(get(action, "type"));

const isPin = (action) =>
  get(action, "type") === ACTION_TYPES.NO_SYNC_PROCESSES;

/**
 * Keeps the web and the advisor journeys aligned, the way classic funnels do.
 * A row either mirrors a step onto another process, or pins that process where
 * it stands. Stored as `SYNC_PROCESSES` / `NO_SYNC_PROCESSES` actions so the
 * canvas and the legacy process editor read the same data.
 */
const MirrorStatusListField = ({
  actions,
  processId,
  variables,
  conditionLabels,
  onChange,
  labels,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [processes, setProcesses] = useState([]);

  const [loadProcesses, { loading }] = useLazyQuery(graphql.queries.PROCESSES, {
    onCompleted: (result) => {
      setProcesses(get(result, "processes.data") || []);
      setLoaded(true);
    },
  });

  const ensureProcesses = () => {
    if (loaded || loading) return;
    loadProcesses({ variables: { take: 500 } });
  };

  const mirrors = (actions || []).filter(isSync);
  const others = (actions || []).filter((action) => !isSync(action));

  const emit = (nextMirrors) => onChange([...others, ...nextMirrors]);

  const replaceAt = (index, patch) =>
    emit(
      mirrors.map((mirror, position) =>
        position === index ? { ...mirror, ...patch } : mirror,
      ),
    );

  const add = (type) => {
    ensureProcesses();
    emit([...mirrors, { id: cuid(), type, args: {}, conditions: [] }]);
  };

  const remove = (index) =>
    emit(mirrors.filter((_, position) => position !== index));

  const processOptions = processes
    .filter(({ id }) => id !== processId)
    .map(({ id, name }) => ({ value: id, label: name || id }));

  const statusOptions = (targetProcessId) =>
    get(
      processes.find(({ id }) => id === targetProcessId),
      "processStatuses",
      [],
    )
      .slice()
      .sort((a, b) => parseInt(a.order, 10) - parseInt(b.order, 10))
      .map(({ id, name }) => ({ value: id, label: name || id }));

  return (
    <div className="canvas-mirror">
      {mirrors.length ? (
        <div className="canvas-mirror__items">
          {mirrors.map((mirror, index) => {
            const targetProcessId = get(mirror, "args.process");
            const pinned = isPin(mirror);
            return (
              <div className="canvas-mirror__item" key={mirror.id || index}>
                <div className="canvas-mirror__row">
                  <CanvasIcon path={ICON_PATHS.sync} strokeWidth={2} />
                  <AntSelect
                    showSearch
                    virtual={false}
                    placeholder={labels.MIRROR_PROCESS}
                    aria-label={labels.MIRROR_PROCESS}
                    optionFilterProp="label"
                    loading={loading}
                    options={processOptions}
                    value={targetProcessId || undefined}
                    onDropdownVisibleChange={ensureProcesses}
                    onChange={(value) =>
                      replaceAt(index, { args: { process: value } })
                    }
                  />
                  <AntSelect
                    showSearch
                    virtual={false}
                    placeholder={
                      pinned ? labels.MIRROR_PIN_STATUS : labels.MIRROR_STATUS
                    }
                    aria-label={labels.MIRROR_STATUS}
                    optionFilterProp="label"
                    disabled={pinned || !targetProcessId}
                    options={statusOptions(targetProcessId)}
                    value={get(mirror, "args.status") || undefined}
                    onDropdownVisibleChange={ensureProcesses}
                    onChange={(value) =>
                      replaceAt(index, {
                        args: { process: targetProcessId, status: value },
                      })
                    }
                  />
                  <button
                    type="button"
                    className="canvas-next__more"
                    aria-label={labels.DELETE}
                    onClick={() => remove(index)}
                  >
                    <CanvasIcon
                      path={ICON_PATHS.trash}
                      size={16}
                      strokeWidth={2}
                    />
                  </button>
                </div>
                <span className="canvas-mirror__hint">
                  {pinned ? labels.MIRROR_PIN_HINT : labels.MIRROR_SYNC_HINT}
                </span>
                <ConditionListEditor
                  conditions={mirror.conditions || []}
                  variables={variables}
                  labels={conditionLabels}
                  onChange={(conditions) => replaceAt(index, { conditions })}
                />
              </div>
            );
          })}
        </div>
      ) : null}
      <div className="canvas-mirror__adders">
        <button
          type="button"
          className="canvas-dashed-button canvas-dashed-button--small"
          onClick={() => add(ACTION_TYPES.SYNC_PROCESSES)}
        >
          <CanvasIcon path={ICON_PATHS.plus} size={16} strokeWidth={2.2} />
          {labels.MIRROR_ADD}
        </button>
        <button
          type="button"
          className="canvas-dashed-button canvas-dashed-button--small"
          onClick={() => add(ACTION_TYPES.NO_SYNC_PROCESSES)}
        >
          <CanvasIcon path={ICON_PATHS.plus} size={16} strokeWidth={2.2} />
          {labels.MIRROR_PIN_ADD}
        </button>
      </div>
    </div>
  );
};

MirrorStatusListField.propTypes = {
  actions: PropTypes.array,
  processId: PropTypes.string,
  variables: PropTypes.array,
  conditionLabels: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  labels: PropTypes.object,
};

MirrorStatusListField.defaultProps = {
  actions: [],
  processId: null,
  variables: [],
  conditionLabels: {},
  labels: {},
};

export default MirrorStatusListField;
